import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, Stepper, ChoiceCards, RadioList, Button, IconButton, Field, TextInput, TextArea, Select, Checkbox, ComboBox, FileDrop,
  Banner, Tag, Spinner, SimpleTable, StatGrid, StatTile, Panel,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { saveAs } from 'file-saver';
import api from '../../services/api.js';
import { formatHebrewDate } from '../../utils/hebrewDate';
import {
  EMPTY_RANGE, EMPTY_LINK, EMPTY_MAX_RULE, EMPTY_STAR_RULE, CHELEK_OPTIONS,
  serializeLibraryLinks, serializeMaxQuestionRules, serializeStarEveryNRules,
} from './issuanceModel.js';
import { FormGrid, MultiCheck, Row } from '../../next/ui';

/**
 * אשף הנפקת בחינות — new design. Same state, payloads and endpoints as ExamIssuanceWizard:
 * 1) exam / period (+ quantities source, city / kolel filters, siman ranges)
 * 2) output (PDF / Excel / Word) + texts, difficulties, star-every-N (+ rules), max questions (+ rules),
 *    design elements (top / bottom / frame, first page only), cover / end pages (library or one-off upload)
 * 3) live preview with per-group overrides → generate (ZIP / xlsx), skipped groups report.
 */
const IS_GEMARA = process.env.REACT_APP_EXAM_MODE === 'open-gemara';
const STEPS = [IS_GEMARA ? 'בחירת מועד מבחנים' : 'בחירת מבחן / מועד', 'בחירת סוג הנפקה', 'תצוגה מקדימה והנפקה'];
const STAR_PRESETS = [{ value: '0', label: 'ללא' }, { value: '2', label: 'כל שני' }, { value: '3', label: 'כל שלישי' }, { value: '4', label: 'כל רביעי' }, { value: '5', label: 'כל חמישי' }, { value: 'custom', label: 'מספר אחר…' }];
const SLOT_LABELS = { top: 'אלמנט למעלה', bottom: 'אלמנט למטה', frame: 'מסגרת' };
const SLOT_SHORT = { top: 'למעלה', bottom: 'למטה', frame: 'מסגרת' };
const digits = (v) => (v === '' ? '' : String(v).replace(/\D/g, ''));
const kolelLabel = (o) => (o.code != null ? `${o.code} — ${o.name || ''}` : (o.name || ''));

function StarEveryNSelect({ value, onChange, label = 'דילוג כוכבית' }) {
  const n = Number(value) || 0;
  const preset = [0, 2, 3, 4, 5].includes(n);
  return (
    <Row>
      <Field label={label}>
        <Select
          value={preset ? String(n) : 'custom'}
          onChange={(v) => { if (v === 'custom') onChange(n >= 2 && !preset ? n : 6); else onChange(Number(v) || 0); }}
          options={STAR_PRESETS}
        />
      </Field>
      {!preset ? (
        <Field label="כל כמה שאלות"><TextInput type="number" ltr min={2} step={1} style={{ width: 120 }} value={n} onChange={(e) => onChange(Math.max(2, parseInt(e.target.value, 10) || 2))} /></Field>
      ) : null}
    </Row>
  );
}

/** code-range rules (shared by max-questions and star-every-N) */
function CodeRules({ rules, setRules, empty, renderValue, emptyText }) {
  const patch = (idx, field, raw) => setRules((rs) => rs.map((r, i) => (i === idx ? { ...r, [field]: digits(raw) } : r)));
  return (
    <div className="nx-stack" style={{ gap: 6 }}>
      <Row>
        <b>לפי טווח קוד מבחן</b>
        <Button size="sm" variant="ghost" icon="plus" onClick={() => setRules((rs) => [...rs, { ...empty }])}>הוסף כלל</Button>
      </Row>
      <small className="ck-muted">הכלל הראשון שמתאים לקוד המבחן מנצח. דריסה פרטנית בתצוגה המקדימה גוברת על הכללים.</small>
      {!rules.length ? <span className="ck-muted">{emptyText}</span> : null}
      {rules.map((rule, idx) => (
        <Row key={idx}>
          <Field label="מקוד"><TextInput type="number" ltr min={0} style={{ width: 96 }} value={rule.fromCode} onChange={(e) => patch(idx, 'fromCode', e.target.value)} /></Field>
          <Field label="עד קוד"><TextInput type="number" ltr min={0} style={{ width: 96 }} value={rule.toCode} onChange={(e) => patch(idx, 'toCode', e.target.value)} /></Field>
          {renderValue(rule, idx, patch)}
          <IconButton size="sm" icon="trash" label="הסר כלל" onClick={() => setRules((rs) => rs.filter((_, i) => i !== idx))} />
        </Row>
      ))}
    </div>
  );
}

function MaxQuestionsSettings({ maxQuestions, setMaxQuestions, rules, setRules }) {
  return (
    <div className="nx-stack">
      <Field label="מספר שאלות מקסימלי (ברירת מחדל)" hint="ריק/0 = כל השאלות; מעל המקסימום — דגימה אקראית">
        <TextInput type="number" ltr min={0} step={1} style={{ width: 120 }} placeholder="ללא הגבלה" value={maxQuestions === 0 ? '' : maxQuestions} onChange={(e) => setMaxQuestions(e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0))} />
      </Field>
      {!IS_GEMARA ? (
        <CodeRules
          rules={rules}
          setRules={setRules}
          empty={EMPTY_MAX_RULE}
          emptyText="אין כללי טווח — כל המבחנים משתמשים בברירת המחדל."
          renderValue={(rule, idx, patch) => <Field label="מקס' שאלות"><TextInput type="number" ltr min={0} style={{ width: 110 }} placeholder="0 = ללא" value={rule.maxQuestions} onChange={(e) => patch(idx, 'maxQuestions', e.target.value)} /></Field>}
        />
      ) : null}
    </div>
  );
}

function PickOne({ label, options, value, onChange, disabled, placeholder = 'בחר…' }) {
  return (
    <Field label={label}>
      <ComboBox value={value} onChange={onChange} options={options} getLabel={(o) => o?.name || ''} getKey={(o) => o?._id} placeholder={placeholder} disabled={disabled} clearable />
    </Field>
  );
}

export default function ExamIssuanceWizardNext({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [skippedWarning, setSkippedWarning] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [kolels, setKolels] = useState([]);
  const [selectedKolels, setSelectedKolels] = useState([]);
  const [sourceMode, setSourceMode] = useState(IS_GEMARA ? 'registrations' : 'both');

  const [outputType, setOutputType] = useState('pdf');
  const [starEveryN, setStarEveryN] = useState(0);
  const [starEveryNRules, setStarEveryNRules] = useState([]);
  const [starEveryNByGroup, setStarEveryNByGroup] = useState({});
  const [designElements, setDesignElements] = useState([]);
  const [activeSlots, setActiveSlots] = useState([]);
  const [elementsLoading, setElementsLoading] = useState(false);
  const [slots, setSlots] = useState({ top: null, bottom: null, frame: null });
  const [firstOnly, setFirstOnly] = useState({ top: true, bottom: true, frame: true });
  const [extraPageTemplates, setExtraPageTemplates] = useState([]);
  const [coverPage, setCoverPage] = useState(null);
  const [endPage, setEndPage] = useState(null);
  const [coverPdfFile, setCoverPdfFile] = useState(null);
  const [endPdfFile, setEndPdfFile] = useState(null);
  const [includeTitle, setIncludeTitle] = useState(true);
  const [includeExamineeCode, setIncludeExamineeCode] = useState(true);
  const [includeExamCode, setIncludeExamCode] = useState(true);
  const [showSimanimHeader, setShowSimanimHeader] = useState(true);
  const [answerLinesCount, setAnswerLinesCount] = useState(4);
  const [maxQuestions, setMaxQuestions] = useState(20);
  const [maxQuestionRules, setMaxQuestionRules] = useState([]);
  const [maxQuestionsByGroup, setMaxQuestionsByGroup] = useState({});
  const [difficulties, setDifficulties] = useState([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState([]);
  const [libraryLinks, setLibraryLinks] = useState([]);
  const [introText, setIntroText] = useState('');
  const [outroText, setOutroText] = useState('');
  const [issuanceName, setIssuanceName] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  /* ── reset + load on open ── */
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setError('');
    setGenerated(false);
    setPreview(null);
    setSkippedWarning(null);
    setIntroText('');
    setOutroText('');
    setOutputType('pdf');
    setSelectedDifficulties([]);
    setLibraryLinks([]);
    setSelectedCities([]);
    setSelectedKolels([]);
    setStarEveryN(0);
    setStarEveryNRules([]);
    setStarEveryNByGroup({});
    setShowSimanimHeader(true);
    setMaxQuestions(20);
    setMaxQuestionRules([]);
    setMaxQuestionsByGroup({});
    setCoverPage(null);
    setEndPage(null);
    setCoverPdfFile(null);
    setEndPdfFile(null);
    (async () => {
      try {
        const [periodsRes, examsRes, diffsRes] = await Promise.all([
          api.get('/exam-periods'),
          IS_GEMARA ? Promise.resolve(null) : api.get('/exams?pageSize=100000'),
          api.get('/exam-library/difficulties'),
        ]);
        setPeriods(periodsRes.data.data || []);
        if (examsRes) setExams(examsRes.data.data || []);
        setDifficulties(Array.isArray(diffsRes.data) ? diffsRes.data : []);
      } catch {
        setError('שגיאה בטעינת נתונים');
      }
      try {
        const { data } = await api.get('/kolels', { params: { pageSize: 500, status: 'active' } });
        setKolels(data.data || []);
      } catch {
        setKolels([]);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setElementsLoading(true);
    Promise.all([api.get('/exam-blanks'), api.get('/exam-extra-pages')])
      .then(([blanksRes, extraRes]) => {
        const list = (blanksRes.data.data || []).filter((e) => e.status !== 'inactive');
        setDesignElements(list);
        const defaults = { top: null, bottom: null, frame: null };
        const on = [];
        ['top', 'bottom', 'frame'].forEach((pos) => {
          const def = list.find((b) => b.position === pos && b.isDefault);
          if (def) { defaults[pos] = def; on.push(pos); }
        });
        setSlots(defaults);
        setActiveSlots(on);
        const extra = (extraRes.data.data || []).filter((e) => e.status !== 'inactive');
        setExtraPageTemplates(extra);
        setCoverPage(extra.find((p) => p.position === 'cover' && p.isDefault) || null);
        setEndPage(extra.find((p) => p.position === 'end' && p.isDefault) || null);
      })
      .catch(() => {})
      .finally(() => setElementsLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const set = new Set();
      try {
        const { data } = await api.get('/registrations?all=1');
        (data.data || []).forEach((r) => { if (r.memberId?.city) set.add(r.memberId.city); if (r.location) set.add(String(r.location).trim()); });
      } catch { /* ignore */ }
      if (!IS_GEMARA) {
        try {
          const { data } = await api.get('/gabbai-orders/summary');
          (data.data || []).forEach((row) => { if (row.location) set.add(String(row.location).trim()); });
        } catch { /* ignore */ }
      }
      setCities([...set].filter(Boolean).sort((a, b) => a.localeCompare(b, 'he')));
    })();
  }, [open]);

  useEffect(() => {
    if (!selectedExam) return;
    setIntroText(selectedExam.introText || '');
    setOutroText(selectedExam.graderNotes || '');
  }, [selectedExam]);

  /* ── siman ranges ── */
  const updateLinks = (fn) => setLibraryLinks((links) => fn(links || []));
  const changeRange = (li, ri, field, val) => updateLinks((links) => links.map((l, i) => (i === li ? { ...l, ranges: l.ranges.map((r, j) => (j === ri ? { ...r, [field]: val } : r)) } : l)));

  /* ── payload ── */
  const buildOptions = useCallback(() => {
    const options = {
      includeTitle,
      includeExamineeCode,
      includeExamCode,
      answerLinesCount,
      maxQuestions,
      difficulties: selectedDifficulties,
      introText,
      outroText,
      showSimanimHeader: !IS_GEMARA && showSimanimHeader,
      starEveryN: starEveryN >= 2 ? starEveryN : 0,
    };
    const links = serializeLibraryLinks(libraryLinks);
    if (links) options.libraryLinks = links;
    const rules = serializeMaxQuestionRules(maxQuestionRules);
    if (rules) options.maxQuestionRules = rules;
    if (Object.keys(maxQuestionsByGroup).length) options.maxQuestionsByGroup = maxQuestionsByGroup;
    const starRules = serializeStarEveryNRules(starEveryNRules);
    if (starRules) options.starEveryNRules = starRules;
    if (Object.keys(starEveryNByGroup).length) options.starEveryNByGroup = starEveryNByGroup;
    return options;
  }, [includeTitle, includeExamineeCode, includeExamCode, answerLinesCount, maxQuestions, maxQuestionRules, maxQuestionsByGroup, selectedDifficulties,
    introText, outroText, showSimanimHeader, libraryLinks, starEveryN, starEveryNRules, starEveryNByGroup]);

  const buildBody = useCallback(() => {
    const body = { filters: {}, options: buildOptions(), sourceMode: IS_GEMARA ? 'registrations' : sourceMode };
    if (selectedCities.length) body.filters.cities = selectedCities;
    if (selectedKolels.length) body.filters.kolels = selectedKolels.map(String);
    if (IS_GEMARA && selectedPeriod) body.examPeriodId = selectedPeriod._id;
    else if (!IS_GEMARA && selectedExam) body.examId = selectedExam._id;
    else if (!IS_GEMARA && selectedPeriod) body.examPeriodId = selectedPeriod._id;
    return body;
  }, [buildOptions, sourceMode, selectedCities, selectedKolels, selectedPeriod, selectedExam]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setError('');
    try {
      const { data } = await api.post('/exam-issuance/preview', buildBody());
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.error || 'שגיאה בטעינת תצוגה מקדימה');
    } finally {
      setPreviewLoading(false);
    }
  }, [buildBody]);
  useEffect(() => {
    if (step !== 2) return undefined;
    const t = setTimeout(() => loadPreview(), 400);
    return () => clearTimeout(t);
  }, [step, loadPreview]);

  const hasValidSimanRange = !IS_GEMARA && !!serializeLibraryLinks(libraryLinks);
  const canProceed0 = !!(selectedPeriod || selectedExam || hasValidSimanRange);
  const next = () => {
    if (step === 0 && !canProceed0) { setError(IS_GEMARA ? 'יש לבחור מועד' : 'יש לבחור מבחן, מועד, או טווח סימנים'); return; }
    setError('');
    setStep((s) => s + 1);
  };
  const back = () => { setError(''); setStep((s) => s - 1); };

  const generate = async () => {
    setGenerating(true);
    setError('');
    try {
      const body = buildBody();
      if (issuanceName.trim()) body.issuanceName = issuanceName.trim();
      if (outputType === 'excel') {
        const response = await api.post('/exam-issuance/export-excel', body, { responseType: 'blob' });
        saveAs(response.data, issuanceName.trim() ? `${issuanceName.trim()}.xlsx` : 'exam-issuance.xlsx');
        setGenerated(true);
        return;
      }
      if (outputType === 'pdf') {
        const de = {};
        ['top', 'bottom', 'frame'].forEach((pos) => { if (slots[pos]) de[pos] = { id: slots[pos]._id, firstPageOnly: firstOnly[pos] }; });
        if (Object.keys(de).length) body.designElements = de;
        const extraPages = {};
        if (!coverPdfFile && coverPage) extraPages.cover = { id: coverPage._id };
        if (!endPdfFile && endPage) extraPages.end = { id: endPage._id };
        if (Object.keys(extraPages).length) body.extraPages = extraPages;
      }
      const endpoint = outputType === 'word' ? '/exam-issuance/generate-word' : '/exam-issuance/generate-pdf';
      let response;
      if (outputType === 'pdf' && (coverPdfFile || endPdfFile)) {
        const fd = new FormData();
        fd.append('payload', JSON.stringify(body));
        if (coverPdfFile) fd.append('coverPdf', coverPdfFile);
        if (endPdfFile) fd.append('endPdf', endPdfFile);
        response = await api.post(endpoint, fd, { responseType: 'blob', headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        response = await api.post(endpoint, body, { responseType: 'blob' });
      }
      if (response.data.type === 'application/json') {
        const errData = JSON.parse(await response.data.text());
        if (errData.skippedGroups) throw new Error(`${errData.error}: ${errData.skippedGroups.map((g) => g.label).join(', ')}`);
        throw new Error(errData.error || 'שגיאה בהנפקה');
      }
      const skippedHeader = response.headers['x-skipped-groups'];
      if (skippedHeader) {
        try { const skipped = JSON.parse(decodeURIComponent(skippedHeader)); if (skipped.length) setSkippedWarning(skipped); } catch { /* ignore */ }
      }
      saveAs(response.data, issuanceName.trim() ? `${issuanceName.trim()}.zip` : `הנפקת-בחינות-${formatHebrewDate(new Date())}.zip`);
      setGenerated(true);
    } catch (err) {
      if (err.response?.data instanceof Blob && err.response.data.type === 'application/json') {
        try {
          const errData = JSON.parse(await err.response.data.text());
          setError(errData.skippedGroups?.length ? `${errData.error}: ${errData.skippedGroups.map((g) => g.label).join(', ')}` : (errData.error || 'שגיאה בהנפקה'));
        } catch {
          setError('שגיאה בהנפקה');
        }
      } else {
        setError(err.message || err.response?.data?.error || 'שגיאה בהנפקה');
      }
    } finally {
      setGenerating(false);
    }
  };

  /* ── per-group overrides ── */
  const groupKey = (g) => g.groupKey || (g.exam?._id ? `exam:${g.exam._id}` : g.tractate ? `tractate:${g.tractate}` : 'no-exam');
  const setOverride = (setter, key, raw, star) => setter((prev) => {
    const nextMap = { ...prev };
    if (raw === '') delete nextMap[key];
    else { const v = Math.max(0, parseInt(raw, 10) || 0); nextMap[key] = star && v < 2 ? 0 : v; }
    return nextMap;
  });

  const close = () => { if (!generating) onClose(); };
  const groups = preview?.groups || [];
  const generateLabel = generating ? 'מנפיק…' : outputType === 'pdf' ? `הנפק ${groups.length} קבצי PDF` : outputType === 'word' ? `הנפק ${groups.length} קבצי Word` : 'הורד Excel';

  const footer = generated ? <Button variant="primary" onClick={() => onClose()}>סגור</Button> : (
    <>
      <Button variant="ghost" disabled={generating} onClick={close}>ביטול</Button>
      {step > 0 ? <Button icon="right" disabled={generating} onClick={back}>חזור</Button> : null}
      {step < 2 ? <Button variant="primary" icon="left" disabled={step === 0 && !canProceed0} onClick={next}>{step === 1 ? 'תצוגה מקדימה' : 'שלב הבא'}</Button> : null}
      {step === 2 ? <Button variant="primary" icon={outputType === 'excel' ? 'download' : 'file'} disabled={generating || !groups.length} onClick={generate}>{generateLabel}</Button> : null}
    </>
  );

  return (
    <Dialog open={open} onClose={close} title="הנפקת בחינות" icon="file" size="lg" footer={footer}>
      <div className="nx-stack">
        <Stepper steps={STEPS} active={step} />

        {step === 0 ? (
          <div className="nx-stack">
            {IS_GEMARA ? (
              <Field label="בחר מועד מבחנים">
                <ComboBox value={selectedPeriod} onChange={(v) => { setSelectedPeriod(v); setSelectedExam(null); }} options={periods} getLabel={(o) => (o ? `${o.name}${o.isActive ? ' (פעיל)' : ''}` : '')} getKey={(o) => o?._id} placeholder="חפש…" clearable />
              </Field>
            ) : (
              <>
                <FormGrid cols={2}>
                  <Field label="בחר מבחן (אופציונלי)">
                    <ComboBox value={selectedExam} onChange={(v) => { setSelectedExam(v); if (v) setSelectedPeriod(null); }} options={exams} getLabel={(o) => (o?.name ? `${o.name} (קוד ${o.code})` : '')} getKey={(o) => o?._id} placeholder="חפש מבחן…" clearable />
                  </Field>
                  <Field label="או בחר מועד (אופציונלי)">
                    <ComboBox value={selectedPeriod} onChange={(v) => { setSelectedPeriod(v); if (v) setSelectedExam(null); }} options={periods} getLabel={(o) => o?.name || ''} getKey={(o) => o?._id} placeholder="חפש מועד…" clearable />
                  </Field>
                </FormGrid>
                <small className="ck-muted">אפשר גם להנפיק לפי טווח סימנים בלבד — בלי מבחן, עיר או כולל.</small>
                <Field label="מקור כמויות להנפקה">
                  <RadioList inline value={sourceMode} onChange={setSourceMode} options={[{ value: 'both', label: 'הרשמות + הזמנות גבאים' }, { value: 'gabbai', label: 'רק הזמנות גבאים' }, { value: 'registrations', label: 'רק הרשמות' }]} />
                </Field>
              </>
            )}

            <FormGrid cols={2}>
              <Panel title="סינון לפי עיר" icon="filter" collapsible defaultOpen={false} actions={selectedCities.length ? <Tag tone="primary">{`${selectedCities.length} ערים נבחרו`}</Tag> : null}>
                <MultiCheck options={cities} value={selectedCities} onChange={setSelectedCities} emptyText="אין ערים" maxHeight={200} />
              </Panel>
              <Panel title="סינון לפי כולל" icon="users" collapsible defaultOpen={false} actions={selectedKolels.length ? <Tag tone="primary">{`${selectedKolels.length} כוללים נבחרו`}</Tag> : null}>
                <MultiCheck options={kolels.map((k) => ({ value: String(k._id), label: kolelLabel(k) }))} value={selectedKolels} onChange={setSelectedKolels} emptyText="אין כוללים" maxHeight={200} />
              </Panel>
            </FormGrid>

            {!IS_GEMARA ? (
              <Panel
                title="טווח סימנים"
                sub="מספיק לבדו להנפקה (בלי מבחן / עיר / כולל). אם נבחר גם מבחן — מחליף את שיוך הספרייה להנפקה זו בלבד."
                icon="book"
                actions={<Button size="sm" variant="ghost" icon="plus" onClick={() => updateLinks((links) => [...links, { ...EMPTY_LINK, ranges: [{ ...EMPTY_RANGE }] }])}>הוסף חלק</Button>}
              >
                {!libraryLinks.length ? <span className="ck-muted">לא הוגדר טווח — נדרש מבחן/מועד, וייעשה שימוש בשיוך הספרייה של המבחן.</span> : (
                  <div className="nx-list">
                    {libraryLinks.map((link, li) => (
                      <div key={li} className="nx-item" style={{ alignItems: 'flex-start' }}>
                        <div className="nx-stack" style={{ flex: 1, gap: 6 }}>
                          <Row>
                            <Field label="חלק"><Select value={link.chelek || ''} onChange={(v) => updateLinks((links) => links.map((l, i) => (i === li ? { ...l, chelek: v } : l)))} options={CHELEK_OPTIONS} /></Field>
                            <Button size="sm" variant="ghost" icon="plus" disabled={!link.chelek} onClick={() => updateLinks((links) => links.map((l, i) => (i === li ? { ...l, ranges: [...(l.ranges || []), { ...EMPTY_RANGE }] } : l)))}>רצף</Button>
                            <span className="ck-grow" />
                            <IconButton size="sm" icon="trash" label="הסר חלק" onClick={() => updateLinks((links) => links.filter((_, i) => i !== li))} />
                          </Row>
                          {(link.ranges || []).map((range, ri) => (
                            <Row key={ri}>
                              <TextInput type="number" ltr min={1} style={{ width: 104 }} placeholder="מסימן" disabled={!link.chelek} value={range.simanFrom || ''} onChange={(e) => changeRange(li, ri, 'simanFrom', digits(e.target.value))} />
                              <span className="ck-muted">—</span>
                              <TextInput type="number" ltr min={1} style={{ width: 104 }} placeholder="עד סימן" disabled={!link.chelek} value={range.simanTo || ''} onChange={(e) => changeRange(li, ri, 'simanTo', digits(e.target.value))} />
                              {(link.ranges || []).length > 1 ? <IconButton size="sm" icon="x" label="הסר רצף" onClick={() => updateLinks((links) => links.map((l, i) => (i === li ? { ...l, ranges: l.ranges.filter((_, j) => j !== ri) } : l)))} /> : null}
                            </Row>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            ) : null}
          </div>
        ) : null}

        {step === 1 ? (
          <div className="nx-stack">
            <Field label="שם להנפקה (אופציונלי)" hint="ישמש כשם תיקיית ה-ZIP ולזיהוי בהיסטוריה">
              <TextInput value={issuanceName} placeholder="לדוג׳: הנפקת ניסן תשפ״ו — ערים מרכז" onChange={(e) => setIssuanceName(e.target.value)} />
            </Field>
            <Field label="סוג הנפקה">
              <ChoiceCards
                value={outputType}
                onChange={setOutputType}
                columns={3}
                options={[
                  { value: 'pdf', label: 'הדפסת בחינה מוכנה', description: 'PDF עם בלאנק, כותרת ושאלות', icon: 'file' },
                  { value: 'excel', label: 'ייצוא ל-Excel', description: 'רשימת נבחנים עם פרטים לייצוא', icon: 'grid' },
                  { value: 'word', label: 'ייצוא ל-Word', description: 'לעריכת שאלות בכוללים', icon: 'edit' },
                ]}
              />
            </Field>

            {outputType !== 'excel' ? (
              <Panel title="טקסט חופשי על הבחינה" icon="note">
                <div className="nx-stack">
                  <Field label="טקסט בתחילת המבחן"><TextArea rows={3} value={introText} placeholder="לדוג׳: מי שיש לו שני מבחנים עונה רק על שאלות עם כוכבית" onChange={(e) => setIntroText(e.target.value)} /></Field>
                  <Field label="טקסט בסוף המבחן" hint={!IS_GEMARA && selectedExam ? 'ממולא אוטומטית מהמבחן — ניתן לערוך' : undefined}><TextArea rows={3} value={outroText} placeholder="הערות והארות לבודקים ולמערכת" onChange={(e) => setOutroText(e.target.value)} /></Field>
                </div>
              </Panel>
            ) : null}

            <Panel title="סינון שאלות" sub="ריק = כל רמות הקושי" icon="filter">
              <MultiCheck options={difficulties} value={selectedDifficulties} onChange={setSelectedDifficulties} emptyText="אין רמות קושי" maxHeight={160} />
            </Panel>

            {outputType !== 'excel' ? (
              <Panel title="כוכבית בתחילת שאלות" sub="מסמן בכוכבית שאלות לפי דילוג (למשל כל שני / כל שלישי). כוכביות שסומנו בספרייה נשמרות." icon="sparkle">
                <div className="nx-stack">
                  <StarEveryNSelect label="דילוג כוכבית (ברירת מחדל)" value={starEveryN} onChange={setStarEveryN} />
                  {!IS_GEMARA ? (
                    <CodeRules
                      rules={starEveryNRules}
                      setRules={setStarEveryNRules}
                      empty={EMPTY_STAR_RULE}
                      emptyText="אין כללי טווח — כל המבחנים משתמשים בברירת המחדל."
                      renderValue={(rule, idx) => <StarEveryNSelect value={rule.starEveryN} onChange={(v) => setStarEveryNRules((rs) => rs.map((r, i) => (i === idx ? { ...r, starEveryN: v } : r)))} />}
                    />
                  ) : null}
                </div>
              </Panel>
            ) : null}

            {outputType === 'word' ? (
              <Panel title="הגדרות Word" icon="edit">
                <div className="nx-stack">
                  <Checkbox checked={includeTitle} onChange={setIncludeTitle} label="הצג כותרת מבחן" />
                  {!IS_GEMARA ? <Checkbox checked={showSimanimHeader} onChange={setShowSimanimHeader} label="הצג סימני המבחן בתחילה (בגדול)" /> : null}
                  <MaxQuestionsSettings maxQuestions={maxQuestions} setMaxQuestions={setMaxQuestions} rules={maxQuestionRules} setRules={setMaxQuestionRules} />
                </div>
              </Panel>
            ) : null}

            {outputType === 'pdf' ? (
              <Panel title="הגדרות הדפסה" icon="file">
                <div className="nx-stack">
                  <h3 className="nx-sub-h">אלמנטים עיצוביים</h3>
                  {elementsLoading ? <Row><Spinner size={16} /><small className="ck-muted">טוען אלמנטים…</small></Row> : !designElements.length ? (
                    <small className="ck-muted">אין אלמנטים עיצוביים — ניתן להוסיף בלשונית &quot;אלמנטים עיצוביים&quot;</small>
                  ) : (
                    <>
                      {['top', 'bottom', 'frame'].filter((pos) => activeSlots.includes(pos)).map((pos) => (
                        <Row key={pos}>
                          <div style={{ flex: 1, minWidth: 200 }}><PickOne label={SLOT_LABELS[pos]} options={designElements.filter((e) => e.position === pos)} value={slots[pos]} onChange={(v) => setSlots((s) => ({ ...s, [pos]: v }))} /></div>
                          <Checkbox checked={firstOnly[pos]} onChange={(v) => setFirstOnly((f) => ({ ...f, [pos]: v }))} label="דף ראשון בלבד" />
                          <IconButton size="sm" icon="trash" label="הסר" onClick={() => { setActiveSlots((s) => s.filter((x) => x !== pos)); setSlots((s) => ({ ...s, [pos]: null })); }} />
                        </Row>
                      ))}
                      <Row>
                        {['top', 'bottom', 'frame'].filter((pos) => !activeSlots.includes(pos) && designElements.some((e) => e.position === pos)).map((pos) => (
                          <Button key={pos} size="sm" variant="ghost" icon="plus" onClick={() => setActiveSlots((s) => [...s, pos])}>{SLOT_SHORT[pos]}</Button>
                        ))}
                      </Row>
                    </>
                  )}

                  <h3 className="nx-sub-h">דפי שער וסיום (PDF)</h3>
                  <small className="ck-muted">בחירה מספרייה או העלאה חד־פעמית להנפקה זו. קובץ חד־פעמי גובר על בחירה מהספרייה.</small>
                  {[
                    ['cover', 'דף שער (לפני)', coverPage, setCoverPage, coverPdfFile, setCoverPdfFile],
                    ['end', 'דף סיום (אחרי)', endPage, setEndPage, endPdfFile, setEndPdfFile],
                  ].map(([pos, label, page, setPage, file, setFile]) => (
                    <FormGrid key={pos} cols={2}>
                      <PickOne label={label} options={extraPageTemplates.filter((e) => e.position === pos)} value={file ? null : page} disabled={!!file} placeholder="בחר מהספרייה…" onChange={(v) => { setPage(v); if (v) setFile(null); }} />
                      <Field label="או העלאה לקובץ זה">
                        <Row>
                          <div style={{ flex: 1 }}><FileDrop accept=".pdf,application/pdf" icon="upload" title={file ? file.name : 'העלה לקובץ זה'} hint="PDF" onFile={(f) => { setFile(f || null); if (f) setPage(null); }} /></div>
                          {file || page ? <IconButton size="sm" icon="trash" label="נקה" onClick={() => { setFile(null); setPage(null); }} /> : null}
                        </Row>
                      </Field>
                    </FormGrid>
                  ))}

                  <div className="nx-grid nx-grid--2">
                    <Checkbox checked={includeTitle} onChange={setIncludeTitle} label="הצג כותרת מבחן" />
                    {!IS_GEMARA ? <Checkbox checked={showSimanimHeader} onChange={setShowSimanimHeader} label="הצג סימני המבחן בתחילה (בגדול)" /> : null}
                    <Checkbox checked={includeExamineeCode} onChange={setIncludeExamineeCode} label="הצג שדה קוד נבחן / ברקוד" />
                    <Checkbox checked={includeExamCode} onChange={setIncludeExamCode} label="הצג קוד מבחן" />
                  </div>
                  <hr className="nx-hr" />
                  <Field label="שורות ריקות למענה" hint="0–20 שורות לכל שאלה">
                    <TextInput type="number" ltr min={0} max={20} step={1} style={{ width: 96 }} value={answerLinesCount} onChange={(e) => setAnswerLinesCount(Math.max(0, Math.min(20, parseInt(e.target.value, 10) || 0)))} />
                  </Field>
                  <MaxQuestionsSettings maxQuestions={maxQuestions} setMaxQuestions={setMaxQuestions} rules={maxQuestionRules} setRules={setMaxQuestionRules} />
                </div>
              </Panel>
            ) : null}
          </div>
        ) : null}

        {step === 2 ? (
          previewLoading && !preview ? <div className="ck-empty"><Spinner size={30} /><span className="ck-muted">טוען תצוגה מקדימה…</span></div> : preview ? (
            <div className="nx-stack">
              {previewLoading ? <Row><Spinner size={14} /><small className="ck-muted">מעדכן מספרי שאלות…</small></Row> : null}
              <StatGrid>
                <StatTile label='סה"כ רשומים' value={preview.totalRegistered ?? 0} icon="users" tone="primary" />
                <StatTile label="אחרי סינון" value={preview.totalFiltered ?? 0} icon="filter" tone={preview.isPartial ? 'warning' : 'success'} sub={preview.isPartial ? 'הנפקה חלקית' : undefined} />
                {!IS_GEMARA ? <StatTile label="הזמנות גבאים" value={preview.totalGabbaiCopies ?? 0} icon="box" tone="info" sub={preview.sourceMode === 'both' ? 'הרשמות + גבאים' : preview.sourceMode === 'gabbai' ? 'רק גבאים' : preview.sourceMode ? 'רק הרשמות' : undefined} /> : null}
                <StatTile label="עותקים ל־PDF" value={groups.reduce((s, g) => s + (g.copies || 0), 0)} icon="file" tone="warning" />
                <StatTile label="קבצים להנפקה" value={groups.length} icon="folder" />
              </StatGrid>

              {groups.some((g) => g.hasQuestions === false) ? (
                <Banner tone="warning" icon="alert">
                  <b>לבחינות הבאות לא ניתן להנפיק — חסרות שאלות או טווח בספרייה:</b>
                  {groups.filter((g) => g.hasQuestions === false).map((g, i) => (
                    <div key={i}>{`• ${g.label} (${g.memberCount || 0} נבחנים${!IS_GEMARA && g.gabbaiCopies ? `, ${g.gabbaiCopies} גבאים` : ''})${g.questionsReasonLabel ? ` — ${g.questionsReasonLabel}` : ''}`}</div>
                  ))}
                </Banner>
              ) : null}

              <SimpleTable
                rowKey={(g) => groupKey(g)}
                rows={groups.map((g, i) => ({ ...g, _idx: i + 1 }))}
                rowClassName={(g) => (g.hasQuestions === false ? 'is-off' : '')}
                maxHeight={420}
                columns={[
                  { key: '_idx', label: '#' },
                  { key: 'label', label: 'תוכן הבחינה', render: (g) => <span><b>{g.label}</b>{g.hasQuestions === false ? <> <Tag tone="warning">אין שאלות</Tag></> : null}</span> },
                  { key: 'questionCount', label: 'שאלות', render: (g) => <Tag tone="primary">{g.questionCount ?? '—'}</Tag> },
                  {
                    key: 'max',
                    label: "מקס' שאלות",
                    render: (g) => { const k = groupKey(g); return <TextInput type="number" ltr min={0} style={{ width: 84 }} title="ריק = לפי ברירת מחדל / טווח קוד; 0 = ללא הגבלה למבחן זה" placeholder={g.appliedMaxQuestions ? String(g.appliedMaxQuestions) : 'ללא'} value={Object.prototype.hasOwnProperty.call(maxQuestionsByGroup, k) ? maxQuestionsByGroup[k] : ''} onChange={(e) => setOverride(setMaxQuestionsByGroup, k, e.target.value)} />; },
                  },
                  {
                    key: 'star',
                    label: 'דילוג כוכבית',
                    render: (g) => { const k = groupKey(g); return <TextInput type="number" ltr min={0} style={{ width: 84 }} title="ריק = לפי ברירת מחדל / טווח קוד; 0 = ללא כוכבית; ≥2 = כל N שאלות" placeholder={g.appliedStarEveryN ? String(g.appliedStarEveryN) : 'ללא'} value={Object.prototype.hasOwnProperty.call(starEveryNByGroup, k) ? starEveryNByGroup[k] : ''} onChange={(e) => setOverride(setStarEveryNByGroup, k, e.target.value, true)} />; },
                  },
                  { key: 'memberCount', label: 'נבחנים', render: (g) => <Tag tone="success" icon="users">{g.memberCount ?? 0}</Tag> },
                  ...(!IS_GEMARA ? [{ key: 'gabbaiCopies', label: 'הזמנות גבאים', render: (g) => <Tag tone="info">{g.gabbaiCopies ?? 0}</Tag> }] : []),
                  { key: 'copies', label: 'עותקים ל־PDF', render: (g) => <Tag tone="warning"><b>{g.copies ?? 0}</b></Tag> },
                ]}
              />

              {generated ? (
                <Banner tone="success" icon="check">
                  <b>ההנפקה הושלמה בהצלחה!</b>
                  <div>{`${preview.isPartial ? 'הנפקה חלקית — ' : ''}${preview.totalFiltered} נבחנים, ${groups.length} קבצים — הקובץ הורד`}</div>
                </Banner>
              ) : null}
              {generated && skippedWarning?.length ? (
                <Banner tone="warning" icon="alert">
                  <b>הבחינות הבאות דולגו:</b>
                  {skippedWarning.map((g, i) => <div key={i}>{`• ${g.label} (${g.memberCount} נבחנים) — ${g.reason}`}</div>)}
                </Banner>
              ) : null}
            </div>
          ) : null
        ) : null}

        {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}
      </div>
    </Dialog>
  );
}
